/**
 * Saving listings from the admin (docs/phase-3-plan.md). Every write goes
 * through here; components never call Firebase directly.
 */
import { useCallback, useMemo, useState } from 'react';
import { addDoc, collection, deleteDoc, deleteField, doc, updateDoc } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, db, storage } from '../config/firebase';
import { MAX_UPLOAD_BYTES } from '../config/constants';
import { AppUser, Listing, ListingStatus } from '../types';
import { NewListingInput, newListing } from '../utils/listingEdit';
import { pickLang } from '../utils/listingDisplay';
import { HistoryEntry, putBackPatch } from '../utils/listingHistory';
import {
  approvePatch,
  ownerDeletePatch,
  rejectPatch,
  restorePatch,
  resubmitPatch,
  visibilityPatch,
  withOwnerAdded,
  withOwnerRemoved,
} from '../utils/superAdmin';
import { LISTINGS_COLLECTION, useOwnerListings } from './useListings';

/** Who saved and when; added to every listing write. */
function auditFields(): Record<string, string> {
  const uid = auth.currentUser?.uid;
  return { updatedAt: new Date().toISOString(), ...(uid ? { updatedBy: uid } : {}) };
}

/** The status a new listing gets: the super admin publishes as a draft; everyone else waits for approval. */
export function newListingStatus(user: Pick<AppUser, 'role'> | null): ListingStatus {
  return user?.role === 'superAdmin' ? 'draft' : 'pending';
}

/** Listings this user owns, sorted by name in the current language. */
export function useMyItems(uid: string | undefined, isHebrew: boolean) {
  const { listings, loading, error } = useOwnerListings(uid);
  const items = useMemo(
    () =>
      // Listings the owner deleted are gone for them (the super admin still has them).
      listings.filter((l) => l.status !== 'deleted').sort((a, b) =>
        pickLang(a.nameEn, a.nameHe, isHebrew).localeCompare(pickLang(b.nameEn, b.nameHe, isHebrew), isHebrew ? 'he' : 'en')
      ),
    [listings, isHebrew]
  );
  return { items, loading, error };
}

// ---------- Photos ----------

const PHOTO_MAX_PX = 1200;

export class PhotoError extends Error {
  constructor(public readonly key: 'fileTooLarge' | 'mustBeImage') {
    super(key);
    this.name = 'PhotoError';
  }
}

/** Shrinks an image to at most 1200px on its longest side, as JPEG (plan section 13). */
async function resizeImage(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, PHOTO_MAX_PX / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not available');
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not resize image'))), 'image/jpeg', 0.85)
  );
}

function photoPath(listingId: string): string {
  return `listings/${listingId}/photo/photo.jpg`;
}

// ---------- Actions ----------

export function useListingActions() {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => setError(null), []);

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    setSaving(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  /**
   * Saves some fields of a listing. Dotted paths (e.g. 'location.addressHe') are
   * allowed. A field set to `undefined` is removed from the listing.
   */
  const updateListing = useCallback(
    (listingId: string, patch: Record<string, unknown>) =>
      run(() => {
        const data = Object.fromEntries(
          Object.entries(patch).map(([key, value]) => [key, value === undefined ? deleteField() : value])
        );
        return updateDoc(doc(db, LISTINGS_COLLECTION, listingId), { ...data, ...auditFields() });
      }),
    [run]
  );

  /** Creates a new listing owned by the signed-in user. Returns its id. */
  const createListing = useCallback(
    (input: NewListingInput, status: ListingStatus) =>
      run(async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) throw new Error('Not signed in');
        const data = newListing(input, [uid], status, new Date().toISOString());
        const created = await addDoc(collection(db, LISTINGS_COLLECTION), { ...data, updatedBy: uid });
        return created.id;
      }),
    [run]
  );

  /** "Duplicate listing": a copy owned by the signed-in user. Returns the new id. */
  /** Uploads (and resizes) the listing photo, replacing any earlier one. */
  const uploadPhoto = useCallback(
    (listingId: string, file: File) => {
      if (!file.type.startsWith('image/')) return Promise.reject(new PhotoError('mustBeImage'));
      if (file.size > MAX_UPLOAD_BYTES) return Promise.reject(new PhotoError('fileTooLarge'));
      return run(async () => {
        const blob = await resizeImage(file);
        const photoRef = ref(storage, photoPath(listingId));
        await uploadBytes(photoRef, blob, { contentType: 'image/jpeg' });
        // A new token each upload, so the new photo shows instead of the cached one.
        const photoUrl = await getDownloadURL(photoRef);
        await updateDoc(doc(db, LISTINGS_COLLECTION, listingId), { photoUrl, ...auditFields() });
        return photoUrl;
      });
    },
    [run]
  );

  const removePhoto = useCallback(
    (listingId: string) =>
      run(async () => {
        try {
          await deleteObject(ref(storage, photoPath(listingId)));
        } catch (err) {
          if ((err as { code?: string })?.code !== 'storage/object-not-found') throw err;
        }
        await updateDoc(doc(db, LISTINGS_COLLECTION, listingId), { photoUrl: deleteField(), ...auditFields() });
      }),
    [run]
  );

  /** Owner: send a rejected listing again (back to 'pending'). */
  const resubmit = useCallback((listingId: string) => updateListing(listingId, resubmitPatch()), [updateListing]);

  /** Owner deletes their listing (plan 16): status 'deleted'; the super admin can bring it back. */
  const deleteOwn = useCallback(
    (listing: Pick<Listing, 'id' | 'status'>) =>
      updateListing(listing.id, ownerDeletePatch(listing.status, new Date().toISOString())),
    [updateListing]
  );

  return { saving, error, clearError, updateListing, createListing, uploadPhoto, removePhoto, resubmit, deleteOwn };
}

/** Super admin only (the rules refuse these for anyone else). Plan 11.2. */
export function useSuperAdminActions() {
  const { saving, error, clearError, updateListing } = useListingActions();
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const approve = useCallback((listingId: string) => updateListing(listingId, approvePatch()), [updateListing]);

  const reject = useCallback(
    (listingId: string, reason?: string) => updateListing(listingId, rejectPatch(reason)),
    [updateListing]
  );

  const setVisible = useCallback(
    (listingId: string, visible: boolean) => updateListing(listingId, visibilityPatch(visible)),
    [updateListing]
  );

  const addOwner = useCallback(
    (listing: Pick<Listing, 'id' | 'ownerUids'>, uid: string) =>
      updateListing(listing.id, { ownerUids: withOwnerAdded(listing.ownerUids, uid) }),
    [updateListing]
  );

  const removeOwner = useCallback(
    (listing: Pick<Listing, 'id' | 'ownerUids'>, uid: string) =>
      updateListing(listing.id, { ownerUids: withOwnerRemoved(listing.ownerUids, uid) }),
    [updateListing]
  );

  /** Brings back a listing its owner deleted, with the status it had. */
  const restore = useCallback(
    (listing: Pick<Listing, 'id' | 'statusBeforeDelete'>) => updateListing(listing.id, restorePatch(listing)),
    [updateListing]
  );

  /** Change history: set the fields of one change back to their old values. */
  const putBack = useCallback(
    (listingId: string, entry: HistoryEntry) => updateListing(listingId, putBackPatch(entry)),
    [updateListing]
  );

  /** Permanent. Hiding is the normal way to remove something; this is for rejected or test listings. */
  const deleteListing = useCallback(async (listingId: string) => {
    setDeleting(true);
    setDeleteError(null);
    try {
      // Its photo first, while the listing still exists for the Storage rule
      // (shul photos and posters live elsewhere and stay).
      await deleteObject(ref(storage, photoPath(listingId))).catch(() => undefined);
      await deleteDoc(doc(db, LISTINGS_COLLECTION, listingId));
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setDeleting(false);
    }
  }, []);

  return {
    saving: saving || deleting,
    error: error || deleteError,
    clearError: () => {
      clearError();
      setDeleteError(null);
    },
    approve,
    reject,
    setVisible,
    addOwner,
    removeOwner,
    putBack,
    restore,
    deleteListing,
  };
}
